import _ from "lodash";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import { findDOMNode } from "react-dom";
import ImmutablePropTypes from "react-immutable-proptypes";

import { asDragSource } from "../../components/dragAndDrop/DraggableComponent";
import Icon from "../../components/interface/Icon";
import ItemCard from "../../components/itemCardView/ItemCardView";
import { NARRATIVE_VIEW } from "../../constants/identifiers";
import orientations from "../../constants/orientations";
import Dimensions, { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import tableDropAction from "../../constants/tableDropAction";
import { immutableRecords, PropTypeShapes } from "../../types";
import { disableWhenIos } from "../../utils/codeSplit";
import firstAncestorOfClass from "../../utils/firstAncestorOfClass";
import { getModifierKeysForEvent } from "../../utils/getModifierKeys";
import hasAncestorOfClass from "../../utils/hasAncestorOfClass";
import NarrativeAddNodeView from "./NarrativeAddNodeView";

const {
  NARRATIVE_CARD_CHILD_X_MARGIN,
  NARRATIVE_CARD_CHILD_Y_MARGIN,
  NARRATIVE_CARD_FILTER_DROP_ADJUSTMENT,
  NARRATIVE_CARD_LINE_DROP_WIDTH,
  NARRATIVE_CARD_NUM_BACKGROUND_COLOURS
} = Dimensions;

// import debugUtils from "../../utils/debugUtils";

const dragCallbacks = {
  beginDrag: _props => {
    // TODO: Do we want to set isDragging or similar...?
  },
  canDrag: (props, dragSource) => {
    return dragSource.component.canDrag();
  },

  getDragSourceParams: props => {
    return {
      itemId: props.id,
      itemType: props.card.itemDisplay.type,
      sourceView: props.viewId,
      sourceViewType: NARRATIVE_VIEW
    };
  },

  getPreviewElement: props => renderNodePreview(props)
};

const renderNodePreview = nodeProps => {
  const cloneProps = _.clone(nodeProps);
  cloneProps.selectedItemIds = null;
  cloneProps.relativeLeft = 0;
  cloneProps.relativeTop = 0;
  cloneProps.onClick = null;
  cloneProps.onSuperClick = null;
  cloneProps.isPreview = true;
  return <CardNode {...cloneProps} />;
};

const getOnChangeLabel = (onChangeLabelFunc, id) => label => onChangeLabelFunc([id], label);

const renderChildCard = (card, narrativePosition, parentProps, onChangeEditing) => {
  return (
    <NarrativeCardView
      key={card.id}
      id={card.id}
      card={card}
      selectedItemIds={card.cards.size > 0 ? parentProps.selectedItemIds : null}
      isActiveView={parentProps.isActiveView}
      selected={Boolean(parentProps.selectedItemIds && parentProps.selectedItemIds.indexOf(card.id) >= 0)}
      position={narrativePosition}
      viewId={parentProps.viewId}
      highlightDropData={parentProps.highlightDropData}
      viewIsDropTarget={parentProps.viewIsDropTarget}
      narrativeViewSettings={parentProps.narrativeViewSettings}
      onClick={parentProps.onClick}
      onCreateItem={parentProps.onCreateItem}
      allowDrag={parentProps.allowDrag}
      onLabelChange={parentProps.onLabelChange}
      availableItems={parentProps.availableItems}
      onSelectAvailableItem={parentProps.onSelectAvailableItem}
      orderedNarrativeTypes={parentProps.orderedNarrativeTypes}
      labels={parentProps.labels}
      onChangeEditing={onChangeEditing}
      autoEditWhenBlank={parentProps.autoEditWhenBlank}
      typesAllowedNarrativeChildren={parentProps.typesAllowedNarrativeChildren}
      fontSize={parentProps.fontSize}
      onShowInspector={parentProps.onShowInspector}
      typesById={parentProps.typesById}
      onBecameActiveView={parentProps.onBecameActiveView}
    />
  );
};

const renderDropLine = (card, index, dropAction, fontSize) => {
  const lineWidth = NARRATIVE_CARD_LINE_DROP_WIDTH;
  const SCALED_NARRATIVE_CARD_FILTER_DROP_ADJUSTMENT = scalePixelsToFontSize(
    NARRATIVE_CARD_FILTER_DROP_ADJUSTMENT,
    fontSize
  );
  // get the card we are placing before
  const childCard = card.cards.get(index);

  let lineLength;
  let x = 0;
  let y = 0;
  const adjustDropLineForFilteredItems = dropAction === tableDropAction.DropAboveRow;

  if (card.orientation === orientations.HORIZONTAL) {
    const tallestCard = card.cards.maxBy(c => c.rect.height);
    const tallestHeight = tallestCard ? tallestCard.rect.height : 0;
    x = childCard
      ? childCard.rect.left - NARRATIVE_CARD_CHILD_X_MARGIN / 2 - NARRATIVE_CARD_LINE_DROP_WIDTH / 2
      : card.cards.last().rect.right + NARRATIVE_CARD_CHILD_X_MARGIN / 2 - NARRATIVE_CARD_LINE_DROP_WIDTH / 2;
    y = NARRATIVE_CARD_CHILD_Y_MARGIN;
    lineLength = `${tallestHeight}px`;
    if (adjustDropLineForFilteredItems) {
      x += SCALED_NARRATIVE_CARD_FILTER_DROP_ADJUSTMENT;
    }
  } else {
    x = 0;
    y = childCard
      ? childCard.rect.top - NARRATIVE_CARD_CHILD_Y_MARGIN / 2 - NARRATIVE_CARD_LINE_DROP_WIDTH / 2
      : card.cards.last().rect.bottom + NARRATIVE_CARD_CHILD_Y_MARGIN / 2 - NARRATIVE_CARD_LINE_DROP_WIDTH / 2;
    lineLength = "100%";
    if (adjustDropLineForFilteredItems) {
      y += SCALED_NARRATIVE_CARD_FILTER_DROP_ADJUSTMENT;
    }
  }
  const style = {
    width: card.orientation === orientations.HORIZONTAL ? lineWidth : lineLength,
    height: card.orientation === orientations.HORIZONTAL ? lineLength : lineWidth,
    left: `${x}px`,
    top: `${y}px`
  };

  return <div key="dropIndicator" className={`narrativeCardDropIndicator`} style={style} />;
};

const isCardFiltered = card => {
  return card.itemDisplay.excludedByFilter;
};

/* eslint-disable react/display-name */
const CardNode = React.forwardRef((forwardedProps, ref) => {
  // Ref forwarding is needed to forwards double clicks to the "+ Add" menu, but is not applied in the drag drop preview
  const useProps = forwardedProps.useProps ? forwardedProps.useProps : forwardedProps;

  const card = useProps.card;
  const itemDisplay = card.itemDisplay;
  const rect = card.rect;
  const typeData = card.typeData;

  const dropData = useProps.highlightDropData;

  let dropOnThisCard = false;
  let drawDropLine = false;

  if (dropData) {
    dropOnThisCard = useProps.id === dropData.targetParent;
    drawDropLine = dropOnThisCard && card.cards.size > 0; // && dropData.targetParent !== dropData.sourceParent;
  }

  const backgroundCount = (useProps.card.depth - 1) % NARRATIVE_CARD_NUM_BACKGROUND_COLOURS;
  const backgroundClass = `background-${backgroundCount}`;

  let classes = ["narrativeCard", backgroundClass];
  if (useProps.selected) {
    classes.push("selected");
  }
  if (card.cards.size === 0) {
    classes.push("leaf");
  }
  if (dropOnThisCard) {
    classes.push("dropTarget");
  }

  const isFiltered = isCardFiltered(card);

  let left;
  let top;
  let transform;

  if (useProps.isPreview) {
    // If this is the preview, we just want it positioned via left/top and not
    // via a transform.
    left = rect.left;
    top = rect.top;
    transform = null;
  } else {
    // The left/top position is only used to take care of the viewport
    // This means that it is never animated, as it is only ever used when scrolling
    // If there is no viewport passed in, then we are at a lower level where we don't need to worry
    // about the viewport.
    left = 0;
    top = 0;
    // The transform is used to position the card correctly, and this can be animated always.
    // The net result is our animation is completely divorced from our scrolling.
    transform = `translate(${rect.left}px,${rect.top}px)`;
  }

  const style = {
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    left: `${left}px`,
    top: `${top}px`,
    opacity: useProps.isDragging ? 0 : 1,
    transform
  };

  const contentCardCardWrapperStyle = {
    opacity: isFiltered ? 0.4 : 1
  };

  const useSettings = useProps.narrativeViewSettings;

  const childNodes = card.cards
    .map(childCard => {
      const narrativePosition = childCard.position;
      return renderChildCard(childCard, narrativePosition, useProps, forwardedProps.onChangeEditing);
    })
    .toArray();

  if (drawDropLine) {
    childNodes.push(renderDropLine(card, dropData.targetIndex, dropData.dropAction, useProps.fontSize));
  }

  const onClick = e => {
    useProps.onClick(useProps.id, getModifierKeysForEvent(e), e);
  };
  // const onParentClick = e => {
  //   useProps.onClick(itemDisplay.super, getModifierKeysForEvent(e), e);
  // };

  let canHaveChildren = true;
  // #!if readonly !== true
// canHaveChildren = useProps.typesAllowedNarrativeChildren.includes(itemDisplay.type);
  // #!endif

  const onToggleAddPopover = _value => {
    if (!useProps.isActiveView) {
      useProps.onBecameActiveView(useProps.viewId);
    }
  };

  return (
    <div
      className={classes.join(" ")}
      style={style}
      onDoubleClick={forwardedProps.onDoubleClick}
      onClick={onClick}
      // onClick={itemDisplay.super ? onParentClick : onClick}
    >
      <div
        className={`narrativeCardSuperBar`}
        // title={itemDisplay.superLabel ? `Parent: ${itemDisplay.superLabel}` : null}
      />
      <div className="narrativeCardContentWrap">
        <div className="narrativeCardCardContentWrap" style={contentCardCardWrapperStyle} onClick={onClick}>
          <Icon className="itemCardTypeIcon" icon={typeData.icon} color={typeData.color} />
          <div className="narrativeCardItemCardWrapper">
            <ItemCard
              rect={itemDisplay.itemCardSize}
              {...useSettings.toObject()}
              label={itemDisplay.label}
              titleMetrics={itemDisplay.titleMetrics}
              dateTextSize={itemDisplay.dateTextSize}
              narrativeTextSize={itemDisplay.narrativeTextSize}
              durationTextSize={itemDisplay.durationTextSize}
              summaryMetrics={itemDisplay.summaryMetrics}
              identifierSize={itemDisplay.identifierSize}
              identifier={itemDisplay.displayId}
              relatedItemsByType={itemDisplay.relatedItemsByType}
              blockingIdentifiers={itemDisplay.blockingIdentifiers}
              blockedByIdentifiers={itemDisplay.blockedByIdentifiers}
              imagePath={itemDisplay.imagePath}
              isCompleted={itemDisplay.isCompleted}
              onLabelChange={getOnChangeLabel(useProps.onLabelChange, card.id)}
              onChangeEditing={forwardedProps.onChangeEditing}
              isActiveView={useProps.isActiveView}
              isSelected={useProps.selected}
              isPreview={useProps.isPreview}
              autoEditWhenBlank={useProps.autoEditWhenBlank}
              typesById={useProps.typesById}
              type={itemDisplay.type}
            />
          </div>
          <div className={`narrativeCardDot ${itemDisplay.color}`} />
        </div>

        <React.Fragment>
          <div
            className={`narrativeCardChildren ${
              useProps.orientation === orientations.HORIZONTAL ? "horizontal" : "vertical"
            }`}
          >
            {childNodes}
          </div>
          {canHaveChildren ? (
            <NarrativeAddNodeView
              id={`narrativeAddNode-${useProps.viewId}-${useProps.id}`}
              onClick={() => {
                useProps.onCreateItem(useProps.card.id, useProps.card.cards.size);
              }}
              availableItems={useProps.availableItems}
              orderedNarrativeTypes={useProps.orderedNarrativeTypes}
              onSelectAvailableItem={option => {
                return useProps.onSelectAvailableItem(option, useProps.card.id, useProps.card.cards.size);
              }}
              onCreateItem={typeId => {
                useProps.onCreateItem(useProps.card.id, useProps.card.cards.size, typeId);
              }}
              label="Add"
              linkStyle={!card.cards.size}
              narrativeViewSettings={useSettings}
              depth={useProps.card.get("depth") - 1}
              ref={ref}
              togglePopoverCallback={onToggleAddPopover}
            />
          ) : null}
        </React.Fragment>
      </div>
    </div>
  );
});

CardNode.propTypes = {
  useProps: PropTypes.object,
  onDoubleClick: PropTypes.func,
  onChangeEditing: PropTypes.func
};

class NarrativeCardViewWithoutDragSource extends PureComponent {
  constructor(props, context) {
    super(props, context);

    this.onChangeEditing = this.onChangeEditing.bind(this);
    this.onDoubleClick = this.onDoubleClick.bind(this);

    this._isEditing = false;
    this._addNode = null;
  }

  componentDidMount() {
    this._node = findDOMNode(this);
  }

  // componentWillReceiveProps(nextProps) {
  //   debugUtils.logPropChanges("NarrativeCardView", this.props, nextProps, true);
  // }

  canDrag() {
    // Disable the drag and drop when editing inside the node
    // The drag and drop may impact on the click event and focusing.
    return !this._isEditing && this.props.allowDrag;
  }

  onChangeEditing(value) {
    this._isEditing = value;
    const { onChangeEditing } = this.props;
    if (onChangeEditing) {
      onChangeEditing(value);
    }
  }

  onDoubleClick(e) {
    // We used to add items on the double click, but it doesn't really add where the user clicks
    // anyway, and there is a big Add button that does the job instead.
    // So double clicking on an item should just trigger the inspector like it does everywhere else
    const clickWasThisCard = firstAncestorOfClass(e.target, "narrativeCard") === this._node;
    const clickWasAddButton = hasAncestorOfClass(e.target, "narrativeAddNode");

    if (clickWasThisCard && !clickWasAddButton && this.props.onShowInspector) {
      this.props.onShowInspector();
    }
  }

  render() {
    return (
      <CardNode
        useProps={this.props}
        onDoubleClick={disableWhenIos(this.onDoubleClick)}
        onChangeEditing={this.onChangeEditing}
        ref={node => (this._addNode = node)}
      />
    );
  }
}

NarrativeCardViewWithoutDragSource.propTypes = {
  id: PropTypes.string.isRequired,
  viewId: PropTypes.string.isRequired,
  card: PropTypeShapes.narrativeCard.isRequired,
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string),
  isActiveView: PropTypes.bool,
  selected: PropTypes.bool.isRequired,
  highlightDropData: PropTypes.instanceOf(immutableRecords.NarrativeDropPositionRecord),
  onClick: PropTypes.func,
  onParentClick: PropTypes.func,
  onCreateItem: PropTypes.func,
  onLabelChange: PropTypes.func,
  onChangeEditing: PropTypes.func,
  autoEditWhenBlank: PropTypes.bool.isRequired,
  isPreview: PropTypes.bool,
  allowDrag: PropTypes.bool.isRequired,
  onShowInspector: PropTypes.func,

  typesById: ImmutablePropTypes.map.isRequired,

  // comes from asDragSource
  isDragging: PropTypes.bool,

  availableItems: ImmutablePropTypes.list,
  orderedNarrativeTypes: ImmutablePropTypes.list,
  labels: ImmutablePropTypes.list.isRequired,
  typesAllowedNarrativeChildren: ImmutablePropTypes.listOf(PropTypes.string).isRequired
};

const NarrativeCardView = asDragSource(NarrativeCardViewWithoutDragSource, NARRATIVE_VIEW, dragCallbacks);

NarrativeCardView.propTypes = NarrativeCardViewWithoutDragSource.propTypes;

export default NarrativeCardView;
